/** @typedef {import("../types/components.js").DickTokComponent} DickTokComponent */

import { createCustomEvent } from "../events/custom_event.js";
import { TapEvent } from "../events/tap_event.js";
import { supportsTouch } from "../utilities/media.js";

const TapGesture = (superclass) => {
  return class extends superclass {
    /** @type {number} Max length of touch to be considered a tap */
    #touchTapThresholdMs = 200;
    /** @type {number} Max delay in ms between taps */
    #doubleTapThresholdMs = 200;

    /** @type {number} Time of the touchstart event as a timestamp in ms */
    #touchStartTime = 0;
    /** @type {number|null} */
    #doubleTapTimerId = null;

    connectedCallback() {
      super.connectedCallback();
      supportsTouch() ? this.#addTouchListeners() : this.#addMouseListeners();
    }

    cancelTap() {
      clearTimeout(this.#doubleTapTimerId);
      this.#doubleTapTimerId = undefined;
    }

    #addTouchListeners() {
      // The browser simulates a click event for mobile touches, but this event
      // is delayed by ~300ms, so to feel responsive we need to add listeners
      // to the touch events specifically.

      const options = {
        passive: true,
        signal: this.disconnectSignal,
      };

      this.addEventListener(
        "touchstart",
        /** @param {TouchEvent} event */ (event) => {
          this.#touchStartTime = Date.now();
        },
        options,
      );

      this.addEventListener(
        "touchend",
        /** @param {TouchEvent} event */ (event) => {
          if (Date.now() - this.#touchStartTime < this.#touchTapThresholdMs) {
            this.#handleTap(event);
          }
          this.#touchStartTime = 0;
        },
        options,
      );
    }

    #addMouseListeners() {
      this.addEventListener(
        "click",
        () => {
          this.#handleTap();
        },
        {
          passive: true,
          signal: this.disconnectSignal,
        },
      );
    }

    /**
     * Handle tap event. If there's no recent tap, sets a timeout after
     * which the tap event will fire. Otherwise, sends a double tap event.
     * @param {TouchEvent|MouseEvent} event
     */
    #handleTap(event) {
      if (this.#doubleTapTimerId) {
        clearTimeout(this.#doubleTapTimerId);
        this.#doubleTapTimerId = undefined;
        this.dispatchEvent(
          createCustomEvent(TapEvent.DOUBLE_TAP, { bubbles: false }, event),
        );
        return;
      }
      this.#doubleTapTimerId = window.setTimeout(() => {
        this.#doubleTapTimerId = undefined;
        this.dispatchEvent(
          createCustomEvent(TapEvent.TAP, { bubbles: false }, event),
        );
      }, this.#doubleTapThresholdMs);
    }
  };
};

export { TapGesture };
